import type { AuthSurface, OtpOrigin } from '@pams/shared/constants';
import type { OfficerIdentityDto, OtpChallengeDto, SessionDto } from '@pams/shared/dtos';
import type { ApiClient } from '../http/apiClient';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE OTP FLOW, AS FIVE TYPED CALLS.
 *
 *  ⚠️ [amended P5b] BEFORE THIS PASS THERE WAS NO SUCH FILE. The dashboard's login page
 *  called `api.post('/auth/request-otp', …)` inline in a mutation body, with the path,
 *  the request shape and the response type all written out at the call site. That was
 *  fine while there was one login screen. With two it is a second place for the surface
 *  discriminator to be got wrong — and `surface` is the field that decides which token
 *  AUDIENCE a code can be redeemed for, so getting it wrong is a privilege question
 *  rather than a typo.
 *
 *  The client is a PARAMETER, exactly as `Scope` is on the API side, so a caller cannot
 *  reach the wrong app's session by importing the wrong module.
 *
 *  ⚠️ `verifyOtp` deliberately does NOT take `surface`. It is frozen onto the OTP row
 *  when the code is sent (`auth.schema.ts` says why): if the client could supply it
 *  again, an officer could request an officer-surface code and redeem it for a dashboard
 *  session, which is the escalation two audiences exist to prevent. Do not "fix" the
 *  asymmetry by adding it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [amended P6c] `origin` IS REQUIRED HERE EVEN THOUGH THE SERVER DEFAULTS IT, and the
 * argument is this file's own, one field over: with two login screens, a field each app must
 * STATE is a field neither can silently get wrong. Defaulting it here would mean the app that
 * forgot it got the other app's branding in its officers' messages, and nothing would say so.
 *
 * ⚠️ IT IS COSMETIC AND `surface` IS NOT — the two sit adjacent in this signature precisely
 * because they must never be confused. `origin` selects one word in an SMS; `surface` selects
 * the token audience. The tuples share no member, so transposing them is a compile error.
 */
export const requestOtp = (
  api: ApiClient,
  input: { identifier: string; surface: AuthSurface; origin: OtpOrigin },
): Promise<OtpChallengeDto> => api.post<OtpChallengeDto>('/auth/request-otp', input);

/**
 * ⚠️ [C1h §6e] `coords` IS OPTIONAL AND THE OFFICER APP IS THE ONLY CALLER THAT EVER SENDS IT.
 *
 * It is written to a login-telemetry row AFTER the session is issued and is read by nothing on
 * the authentication path — which is what distinguishes it from `surface` above, whose absence
 * is a privilege guarantee. `pwa/src/lib/loginLocation.ts` is the only producer, and it
 * NEVER PROMPTS: it sends a position only where the device ALREADY held permission.
 *
 * ⚠️ OPTIONAL RATHER THAN NULLABLE, so the dashboard's request and all six e2e fixtures move
 * not one byte — `undefined` is omitted by `JSON.stringify`. [P6d]'s test.
 */
export const verifyOtp = (
  api: ApiClient,
  input: { requestId: string; code: string; coords?: { lat: number; lng: number } },
): Promise<SessionDto> => api.post<SessionDto>('/auth/verify-otp', input);

/**
 * `allSessions` ends every session for this officer on every surface. The caller decides;
 * both apps pass `false` for an ordinary sign-out.
 */
export const logout = (api: ApiClient, input: { refreshToken?: string; allSessions: boolean }): Promise<null> =>
  api.post<null>('/auth/logout', input);

/** Serves BOTH surfaces — `requireAuth(ANY_SURFACE)`. */
export const fetchMe = (api: ApiClient): Promise<OfficerIdentityDto> => api.get<OfficerIdentityDto>('/auth/me');
